import React, { useEffect, useCallback } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { Tappable } from '@vectord/components';
import cogoToast from 'cogo-toast';
import { VIEW_TYPES } from '@vectord/analytics';

import { DEFAULT_ERROR_MESSAGE } from 'data/defaultErrorMessage';
import {
  BULK_DELETE_SBAT_GROUP_MODAL_STAGES,
  resetSbatGroupBulkUpdate,
  sbatGroupBulkUpdateCommunicationsSelector,
  sbatGroupBulkUpdateReasonSelector,
  selectedBulkUpdateSbatGroupsSelector,
  setDeleteSbatGroupsErrorMessage,
  setDeleteSbatGroupsModalStage,
  useBulkDeleteSbatGroupsMutation,
  useValidateDestroyMutation,
  VIEW_SCHEDULE_PAGE_TRACKINGS,
} from 'features/classCreation';
import analytics from 'utils/analytics';
import GlobalLoader from 'components/GlobalLoader';

export default function BulkDeleteSbatGroupsButton() {
  const dispatch = useDispatch();
  const [
    bulkDeleteSbatGroup,
    { isSuccess: isDeleteSuccess, isLoading: isDeleting, error },
  ] = useBulkDeleteSbatGroupsMutation();

  const [validateDestroy, { data: response, isLoading: isValidating }] =
    useValidateDestroyMutation();

  const selectedSbatGroups = useSelector(selectedBulkUpdateSbatGroupsSelector);
  const communications = useSelector(sbatGroupBulkUpdateCommunicationsSelector);
  const bulkDeleteReason = useSelector(sbatGroupBulkUpdateReasonSelector);

  useEffect(() => {
    if (response?.success) {
      const comms = Object.keys(communications).filter(
        (key) => communications[key]
      );
      const sbatGroupIds = Object.keys(selectedSbatGroups);

      analytics.view({
        view_type: VIEW_TYPES.modal,
        view_name: VIEW_SCHEDULE_PAGE_TRACKINGS.bulkDeleteClasses,
        custom: {
          class_ids: sbatGroupIds,
          num_of_classes: sbatGroupIds.length,
          comms,
          reason: bulkDeleteReason?.label,
        },
      });

      bulkDeleteSbatGroup({
        sbatGroupIds: sbatGroupIds,
        reason: bulkDeleteReason?.label,
        comms,
      });
    } else if (response?.success === false) {
      batch(() => {
        dispatch(
          setDeleteSbatGroupsModalStage(
            BULK_DELETE_SBAT_GROUP_MODAL_STAGES.confirm
          )
        );
        dispatch(setDeleteSbatGroupsErrorMessage(response?.message));
      });
    }
  }, [
    bulkDeleteReason?.label,
    bulkDeleteSbatGroup,
    communications,
    dispatch,
    response,
    selectedSbatGroups,
  ]);

  useEffect(() => {
    if (isDeleteSuccess) {
      dispatch(resetSbatGroupBulkUpdate());
      cogoToast.success('Selected classes are deleted successfully!');
    }
  }, [dispatch, isDeleteSuccess]);

  useEffect(() => {
    if (error) {
      cogoToast.error(error?.data?.message || DEFAULT_ERROR_MESSAGE);
    }
  }, [error]);

  const handleConfirm = useCallback(() => {
    const sbatGroupIds = Object.keys(selectedSbatGroups);

    validateDestroy({ sbatGroupIds });
  }, [selectedSbatGroups, validateDestroy]);

  return (
    <>
      <GlobalLoader isVisible={isValidating || isDeleting} />
      <Tappable
        className="btn btn-danger btn-long m-b-xxs"
        onClick={handleConfirm}
        disabled={isValidating || isDeleting}
      >
        Delete
      </Tappable>
    </>
  );
}
